import { removeRemindersFromMap } from "../../utils/reminderMapHelpers.js";
import { motion } from "framer-motion";
import { useContext, useEffect, useMemo, useState } from "react";
import { BiAlarmSnooze, BiCheckCircle } from "react-icons/bi";
import { FaTrashAlt } from "react-icons/fa";
import { IoIosAddCircle } from "react-icons/io";
import DatesContext from "../../context/DatesContext.jsx";
import InteractiveContext from "../../context/InteractiveContext.jsx";
import UserContext from "../../context/UserContext.jsx";
import { BsCalendarDay, BsCalendarMonth, BsCalendarWeek } from "react-icons/bs";
import GroupedReminders from "./GroupedReminders.jsx";
import Reminder from "./Reminder.jsx";
import { MdOutlineClearAll } from "react-icons/md";
import { useModalActions } from "../../context/ContextHooks/ModalContext.jsx";
import { LocalStorage_UpdateGroupingPreferences } from "../../utils/localStorageHelpers.ts";
import { deleteRemindersBulk } from "../../utils/api";

const Reminders = ({ sort, sortOpt, search, searchTxt }) => {
  const { remindersById, setRemindersMap, preferences } = useContext(UserContext);
  const [deletingCompleted, setDeletingCompleted] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  // const { preferences } = useContext(UserContext); // Created for testing reminders
  const { dateObj, string, setString } = useContext(DatesContext);
  const { setType, setMenu, setAddNewEvent } = useContext(InteractiveContext);

  // Uncomment for reminder testing purposes and remove reminders from UserContext import
  // const reminders = [
  //   {
  //     id: "1",
  //     title: "Test repeat",
  //     notes: "This is my repeating reminder",
  //     time: new Date().toString(),
  //     snoozes: { count: 0, snoozes: [] },
  //     paused: false,
  //     ignoreDates: [],
  //     ignoreDevices: [],
  //     repeat: {
  //       on: true,
  //       howOften: "Daily",
  //       interval: 6,
  //       skipDates: [],
  //     },
  //     completed: false,
  //     groupId: "",
  //     eventRefId: "",
  //   },
  // ];

  const { openModal } = useModalActions();

  // Pull this from user preferences in the future
  const [grouping, setGrouping] = useState({
    complete: preferences.reminders?.complete?.groupType || "week",
    incomplete: preferences.reminders?.incomplete?.groupType || "week",
  });

  const reminders = useMemo(() => [...remindersById.values()], [remindersById]);
  const incompleteReminders = useMemo(() => {
    return reminders.filter((r) => !r.completed);
  }, [reminders, grouping]);
  const urgentReminders = useMemo(() => {
    const now = Date.now();
    const soon = now + 24 * 60 * 60 * 1000;
    return reminders.filter((r) => !r.completed && !r.paused && Number.isFinite(new Date(r.time).getTime()) && new Date(r.time).getTime() <= soon).sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [reminders]);
  const urgentIds = useMemo(() => new Set(urgentReminders.map((r) => r.id)), [urgentReminders]);
  const regularReminders = useMemo(() => incompleteReminders.filter((r) => !urgentIds.has(r.id)), [incompleteReminders, urgentIds]);
  const completedRemindersToRender = useMemo(() => {
    return reminders.filter((r) => r.completed);
  }, [reminders, grouping]);

  const eligibleForBulkDelete = useMemo(() => {
    const now = Date.now();
    return reminders.filter((reminder) => {
      const due = new Date(reminder.time).getTime();
      return reminder.completed === true && !reminder.eventRefId &&
        Number.isFinite(due) && due < now && reminder.id != null;
    });
  }, [reminders]);

  const deleteOldCompleted = async () => {
    if (deletingCompleted || !eligibleForBulkDelete.length) return;
    setDeletingCompleted(true);
    setDeleteError("");
    const ids = [...new Set(eligibleForBulkDelete.map((reminder) => reminder.id))];
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("You must be signed in to delete reminders.");
      await deleteRemindersBulk(ids, token);
      setRemindersMap((previous) => removeRemindersFromMap(previous, ids));
      setConfirmBulkDelete(false);
    } catch (error) {
      setDeleteError(error?.response?.data?.message || error.message || "Could not delete reminders. Please try again.");
    } finally {
      setDeletingCompleted(false);
    }
  };

  const openModalAndSetType = () => {
    if (!string) {
      setString(dateObj.toLocaleDateString());
    }
    setType("reminder");
    setMenu(false);
    openModal();
    setAddNewEvent(true);
  };

  const updateGroup = (complete, type) => {
    const newGrouping = {
      complete: complete ? type : grouping.complete,
      incomplete: complete ? grouping.incomplete : type,
    };

    setGrouping(newGrouping);

    LocalStorage_UpdateGroupingPreferences(newGrouping);
  };

  return (
    <motion.div className="mt-6 px-3 sm:px-6 mb-96">
      {/* Center container to prevent "stretching across the universe" */}
      <div className="mx-auto max-w-6xl">
        {urgentReminders.length > 0 && <section className="rounded-3xl border border-amber-400/40 bg-amber-100/10 p-4 sm:p-6 mt-6"><p className="text-xs font-bold tracking-widest uppercase calendar-accent">Needs attention</p><h2 className="text-xl font-semibold mt-1">Overdue & next 24 hours</h2><div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">{urgentReminders.map((reminder) => <Reminder key={reminder.id} reminder={reminder} showOpenEvent={true} styles={{}} />)}</div></section>}
        {incompleteReminders.length < 1 ? (
          <div className="min-h-[55vh] grid place-items-center">
            <div
              className={`
            w-full max-w-md rounded-3xl border shadow-2xl backdrop-blur-md p-5 sm:p-6
            ${preferences.darkMode ? "bg-[#161616]/90 border-white/10 text-white" : "bg-white/90 border-black/10 text-slate-900"}
          `}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div
                    className={`
                  grid place-items-center h-12 w-12 rounded-2xl border shadow-sm
                  ${preferences.darkMode ? "bg-rose-500/15 border-rose-300/20 text-rose-100" : "bg-rose-50 border-rose-200 text-rose-700"}
                `}
                  >
                    <BiAlarmSnooze className="text-2xl" />
                  </div>

                  <div>
                    <h2 className="text-lg font-semibold tracking-tight">
                      No upcoming reminders
                    </h2>
                    <p
                      className={`text-sm mt-1 ${preferences.darkMode ? "text-white/60" : "text-slate-500"}`}
                    >
                      Create one and we’ll keep you on track.
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => openModalAndSetType()}
                  className={`
                grid place-items-center h-11 w-11 rounded-2xl border shadow-md transition
                hover:scale-[1.02] active:scale-[0.97]
                ${preferences.darkMode ? "bg-white/10 border-white/10 hover:bg-white/15 text-white" : "bg-black/[0.03] border-black/10 hover:bg-black/[0.06] text-slate-700"}
              `}
                  aria-label="Add reminder"
                >
                  <IoIosAddCircle className="text-2xl" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex flex-wrap gap-y-5 justify-between items-start gap-3 mb-3 mt-40">
              <div className="flex gap-x-5 items-center">
                <div
                  className={`
                  grid place-items-center h-12 w-12 rounded-2xl border shadow-sm
                  ${preferences.darkMode ? "bg-cyan-500/15 border-cyan-300/20 text-cyan-100" : "bg-cyan-50 border-cyan-200 text-cyan-700"}
                `}
                >
                  <BiAlarmSnooze className="text-2xl" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold tracking-tight">
                    Active Reminders
                  </h2>
                  <p
                    className={`text-sm mt-1 ${preferences.darkMode ? "text-white/60" : "text-slate-500"}`}
                  >
                    Here are your active reminders
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {/* Delete Completed */}
                <button
              type="button"
              onClick={() => { setDeleteError(""); setConfirmBulkDelete(true); }}
              disabled={deletingCompleted || eligibleForBulkDelete.length === 0}
              title="Delete past completed reminders not attached to events"
                  className={`
                flex flex-col items-center justify-center
                h-14 w-14 rounded-2xl border shadow-sm
                text-[10px] font-semibold gap-1
                transition active:scale-[0.95] disabled:opacity-40 disabled:cursor-not-allowed
                ${
                  preferences.darkMode
                    ? "bg-white/5 border-white/10 text-white/70 hover:bg-rose-500/20 hover:text-rose-200"
                    : "bg-white border-black/10 text-slate-600 hover:bg-rose-50 hover:text-rose-600"
                }
              `}
                >
                  <MdOutlineClearAll className="text-sm" />
                  Delete All
                </button>
                {/* Clear Grouping */}
                <button
                  onClick={() => updateGroup(false, null)}
                  className={`
                    flex flex-col items-center justify-center
                    h-14 w-14 rounded-2xl border shadow-sm
                    text-[10px] font-semibold gap-1
                    transition-colors active:scale-[0.95]
                    ${
                      grouping.incomplete === null
                        ? preferences.darkMode
                          ? "bg-orange-500/20 border-orange-400/30 text-orange-200"
                          : "bg-orange-50 border-orange-200 text-orange-600"
                        : preferences.darkMode
                          ? "bg-white/5 border-white/10 text-white/70 hover:bg-orange-500/20 hover:text-orange-200"
                          : "bg-white border-black/10 text-slate-600 hover:bg-orange-50 hover:text-orange-600"
                    }
                  `}
                >
                  <FaTrashAlt className="text-sm" />
                  Clear
                </button>

                {/* Group by Day */}
                <button
                  onClick={() => updateGroup(false, "day")}
                  className={`
  flex flex-col items-center justify-center
  h-14 w-14 rounded-2xl border shadow-sm
  text-[10px] font-semibold gap-1
  transition-colors active:scale-[0.95]
  ${
    grouping.incomplete === "day"
      ? preferences.darkMode
        ? "bg-cyan-500/20 border-cyan-400/30 text-cyan-200"
        : "bg-cyan-50 border-cyan-200 text-cyan-600"
      : preferences.darkMode
        ? "bg-white/5 border-white/10 text-white/70 hover:bg-cyan-500/20 hover:text-cyan-200"
        : "bg-white border-black/10 text-slate-600 hover:bg-cyan-50 hover:text-cyan-600"
  }
`}
                >
                  <BsCalendarDay className="text-sm" />
                  Day
                </button>

                {/* Group by Week */}
                <button
                  onClick={() => updateGroup(false, "week")}
                  className={`
  flex flex-col items-center justify-center
  h-14 w-14 rounded-2xl border shadow-sm
  text-[10px] font-semibold gap-1
  transition-colors active:scale-[0.95]
  ${
    grouping.incomplete === "week"
      ? preferences.darkMode
        ? "bg-indigo-500/20 border-indigo-400/30 text-indigo-200"
        : "bg-indigo-50 border-indigo-200 text-indigo-600"
      : preferences.darkMode
        ? "bg-white/5 border-white/10 text-white/70 hover:bg-indigo-500/20 hover:text-indigo-200"
        : "bg-white border-black/10 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600"
  }
`}
                >
                  <BsCalendarWeek className="text-sm" />
                  Week
                </button>

                {/* Group by Month */}
                <button
                  onClick={() => updateGroup(false, "month")}
                  className={`
  flex flex-col items-center justify-center
  h-14 w-14 rounded-2xl border shadow-sm
  text-[10px] font-semibold gap-1
  transition-colors active:scale-[0.95]
  ${
    grouping.incomplete === "month"
      ? preferences.darkMode
        ? "bg-amber-500/20 border-amber-400/30 text-amber-200"
        : "bg-amber-50 border-amber-200 text-amber-600"
      : preferences.darkMode
        ? "bg-white/5 border-white/10 text-white/70 hover:bg-amber-500/20 hover:text-amber-200"
        : "bg-white border-black/10 text-slate-600 hover:bg-amber-50 hover:text-amber-600"
  }
`}
                >
                  <BsCalendarMonth className="text-sm" />
                  Month
                </button>
              </div>
            </div>
            <GroupedReminders
              groupType={grouping.incomplete}
              reminders={regularReminders}
            />
          </div>
        )}
        <div className="flex flex-wrap gap-y-5 justify-between items-start gap-3 mb-3 mt-40">
          <div className="flex gap-x-5 items-center">
            <div
              className={`
                  grid place-items-center h-12 w-12 rounded-2xl border shadow-sm
                  ${preferences.darkMode ? "bg-cyan-500/15 border-cyan-300/20 text-cyan-100" : "bg-cyan-50 border-cyan-200 text-cyan-700"}
                `}
            >
              <BiCheckCircle className="text-2xl" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight">
                Completed Reminders
              </h2>
              <p
                className={`text-sm mt-1 ${preferences.darkMode ? "text-white/60" : "text-slate-500"}`}
              >
                Here are your complete reminders
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {/* Delete Completed */}
            <button
              className={`
                flex flex-col items-center justify-center
                h-14 w-14 rounded-2xl border shadow-sm
                text-[10px] font-semibold gap-1
                transition active:scale-[0.95]
                ${
                  preferences.darkMode
                    ? "bg-white/5 border-white/10 text-white/70 hover:bg-rose-500/20 hover:text-rose-200"
                    : "bg-white border-black/10 text-slate-600 hover:bg-rose-50 hover:text-rose-600"
                }
              `}
            >
              <MdOutlineClearAll className="text-sm" />
              Delete All
            </button>
            {/* Clear Grouping */}
            <button
              onClick={() => updateGroup(true, null)}
              className={`
  flex flex-col items-center justify-center
  h-14 w-14 rounded-2xl border shadow-sm
  text-[10px] font-semibold gap-1
  transition-colors active:scale-[0.95]
  ${
    grouping.complete === null
      ? preferences.darkMode
        ? "bg-orange-500/20 border-orange-400/30 text-orange-200"
        : "bg-orange-50 border-orange-200 text-orange-600"
      : preferences.darkMode
        ? "bg-white/5 border-white/10 text-white/70 hover:bg-orange-500/20 hover:text-orange-200"
        : "bg-white border-black/10 text-slate-600 hover:bg-orange-50 hover:text-orange-600"
  }
`}
            >
              <FaTrashAlt className="text-sm" />
              Clear
            </button>

            {/* Group by Day */}
            <button
              onClick={() => updateGroup(true, "day")}
              className={`
  flex flex-col items-center justify-center
  h-14 w-14 rounded-2xl border shadow-sm
  text-[10px] font-semibold gap-1
  transition-colors active:scale-[0.95]
  ${
    grouping.complete === "day"
      ? preferences.darkMode
        ? "bg-cyan-500/20 border-cyan-400/30 text-cyan-200"
        : "bg-cyan-50 border-cyan-200 text-cyan-600"
      : preferences.darkMode
        ? "bg-white/5 border-white/10 text-white/70 hover:bg-cyan-500/20 hover:text-cyan-200"
        : "bg-white border-black/10 text-slate-600 hover:bg-cyan-50 hover:text-cyan-600"
  }
`}
            >
              <BsCalendarDay className="text-sm" />
              Day
            </button>

            {/* Group by Week */}
            <button
              onClick={() => updateGroup(true, "week")}
              className={`
  flex flex-col items-center justify-center
  h-14 w-14 rounded-2xl border shadow-sm
  text-[10px] font-semibold gap-1
  transition-colors active:scale-[0.95]
  ${
    grouping.complete === "week"
      ? preferences.darkMode
        ? "bg-indigo-500/20 border-indigo-400/30 text-indigo-200"
        : "bg-indigo-50 border-indigo-200 text-indigo-600"
      : preferences.darkMode
        ? "bg-white/5 border-white/10 text-white/70 hover:bg-indigo-500/20 hover:text-indigo-200"
        : "bg-white border-black/10 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600"
  }
`}
            >
              <BsCalendarWeek className="text-sm" />
              Week
            </button>

            {/* Group by Month */}
            <button
              onClick={() => updateGroup(true, "month")}
              className={`
  flex flex-col items-center justify-center
  h-14 w-14 rounded-2xl border shadow-sm
  text-[10px] font-semibold gap-1
  transition-colors active:scale-[0.95]
  ${
    grouping.complete === "month"
      ? preferences.darkMode
        ? "bg-amber-500/20 border-amber-400/30 text-amber-200"
        : "bg-amber-50 border-amber-200 text-amber-600"
      : preferences.darkMode
        ? "bg-white/5 border-white/10 text-white/70 hover:bg-amber-500/20 hover:text-amber-200"
        : "bg-white border-black/10 text-slate-600 hover:bg-amber-50 hover:text-amber-600"
  }
`}
            >
              <BsCalendarMonth className="text-sm" />
              Month
            </button>
          </div>
        </div>
        {deleteError && <p role="alert" className="my-3 text-sm text-red-500">{deleteError}</p>}
        {confirmBulkDelete && <div className={`my-4 rounded-2xl border p-4 ${preferences.darkMode ? "bg-[#202020] border-white/15" : "bg-white border-black/15"}`} role="alertdialog" aria-modal="true" aria-label="Confirm bulk reminder deletion">
          <p className="font-semibold">Delete {eligibleForBulkDelete.length} old completed reminders?</p>
          <p className="text-sm opacity-70 mt-2">Only completed reminders with past due dates and no linked event will be deleted. This cannot be undone.</p>
          <div className="flex flex-wrap gap-3 mt-4">
            <button type="button" disabled={deletingCompleted} onClick={() => setConfirmBulkDelete(false)} className="rounded-xl border px-4 py-2 text-sm">Cancel</button>
            <button type="button" disabled={deletingCompleted || !eligibleForBulkDelete.length} onClick={deleteOldCompleted} className="rounded-xl bg-red-600 text-white px-4 py-2 text-sm disabled:opacity-50">{deletingCompleted ? "Deleting…" : "Delete reminders"}</button>
          </div>
        </div>}
        <GroupedReminders
          groupType={grouping.complete}
          reminders={completedRemindersToRender}
        />
      </div>
    </motion.div>
  );
};

export default Reminders;
